import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { FaPlay } from 'react-icons/fa'
import { RiArrowGoBackLine } from 'react-icons/ri'
import Swiper from 'swiper'
import 'swiper/css'
import Preview from '@renderer/components/Preview'
import { Countdown } from '@renderer/utils/countdownUtils'
import { useConfigStore } from '@renderer/stores/configState'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import { useCachedImage } from '@renderer/hooks/useCachedImage'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'

const FilterPage: React.FC = () => {
  const navigate = useNavigate()
  const [activeIndex, setActiveIndex] = useState(0)
  const [remainingTime, setRemainingTime] = useState<string>('--:--')
  const swiperRef = useRef<Swiper | null>(null)
  const { filters, getPageSettings } = useConfigStore()
  const { setSessionState } = useSessionStateStore()
  const capturePreview = unifiedCameraUtils.capturePreview
  const filterTimeout = getPageSettings('filter')?.filterTimeout || 0
  const countdownInstance = useRef<Countdown | null>(null)
  const { projectId } = useParams()

  useEffect((): (() => void) => {
    swiperRef.current = new Swiper('.mainSwiper', {
      loop: false,
      slidesPerView: 3,
      centeredSlides: true,
      spaceBetween: 10,
      direction: 'horizontal'
    })

    swiperRef.current.on('realIndexChange', (swiper) => {
      setActiveIndex(swiper.realIndex)
    })

    if (filters.length > 0) {
      swiperRef.current.slideToLoop(1)
    }

    if (filterTimeout > 0) {
      countdownInstance.current = new Countdown(
        filterTimeout,
        () => setRemainingTime(countdownInstance.current!.getFormattedRemainingSeconds()),
        () => {
          setSessionState({
            timeline: {
              activityLogs: [
                {
                  page: 'filter',
                  type: 'selection',
                  action: 'timeout',
                  details: {
                    filterId: filters[activeIndex]._id,
                    timeoutDuration: filterTimeout
                  },
                  timestamp: new Date()
                }
              ]
            }
          })
          continueToPhotoSession()
        }
      )
      countdownInstance.current.start()
    }

    return () => {
      if (swiperRef.current) swiperRef.current.destroy()
      countdownInstance.current?.stop()
    }
  }, [])

  const continueToPhotoSession = async (): Promise<void> => {
    const selectedFilter = filters[activeIndex]
    setSessionState({
      userPreferences: {
        filterId: selectedFilter._id
      },
      timeline: {
        activityLogs: [
          {
            page: 'filter',
            type: 'selection',
            action: 'complete',
            details: { filterId: selectedFilter._id },
            timestamp: new Date()
          }
        ]
      }
    })

    navigate(`/${projectId}/frame`)
  }

  const slidePrev = (): void => {
    swiperRef.current?.slidePrev()
  }

  const slideNext = (): void => {
    swiperRef.current?.slideNext()
  }

  const handleClick = (index: number): void => {
    swiperRef.current?.slideToLoop(index)
  }

  const cachedBackground = useCachedBackgroundImage('/assets/samu/samu-filter.webp')
  const cachedLogo = useCachedImage('/assets/samu/samu-logo.png')

  return (
    <div
      className="relative w-full h-full bg-cover bg-center"
      style={{ backgroundImage: `url(${cachedBackground})` }}
    >
      <img alt="Samu Logo" src={cachedLogo} width={240} height={240} className="absolute right-6" />
      <div className="absolute z-[5] left-[50px] top-[50px]">
        <button
          onClick={() => navigate(`/${projectId}`)}
          className="flex items-center gap-3 rounded-full bg-carnabyTan px-6 py-3 text-juicyMango hover:opacity-90 transition-opacity"
        >
          <RiArrowGoBackLine size={36} />
          <span className="font-body-samu text-3xl">Back</span>
        </button>
      </div>

      <div className="absolute top-[250px] left-1/2 -translate-x-1/2 w-screen h-[600px] flex justify-between items-center">
        <button
          onClick={slidePrev}
          className="rounded-full bg-carnabyTan py-6 pl-5 pr-7 mx-8 hover:opacity-90 transition-opacity"
          aria-label="Previous filter"
        >
          <FaPlay className="text-juicyMango w-8 h-8 rotate-180" />
        </button>
        <div className="swiper mainSwiper w-full flex items-center justify-center">
          <div className="swiper-wrapper cursor-grab p-0 active:cursor-grabbing focus:cursor-grabbing w-full transition-all duration-300">
            {filters.map((filter, index) => (
              <div
                className="swiper-slide flex flex-col p-0 items-center justify-center transition-all h-[770px] duration-300"
                key={filter._id}
              >
                <div
                  onClick={() => handleClick(index)}
                  className={`rounded-2xl px-6 py-4 flex flex-col items-center justify-center transition-all duration-300 w-fit
                    ${index % 2 === 0 ? 'bg-orangePop' : 'bg-poppyGlow'}
                    ${activeIndex === index ? '' : 'mt-8'}`}
                >
                  <div className="text-center relative w-auto mb-4">
                    <h2
                      className={`text-carnabyTan font-deco-samu transition-all duration-300 
                      ${activeIndex === index ? 'text-6xl' : 'text-4xl'}`}
                    >
                      {filter.name}
                    </h2>
                  </div>
                  <div
                    className={`rounded-2xl overflow-hidden object-contain relative
                    ${activeIndex === index ? 'w-[320px] h-[320px]' : 'w-[293px] h-[293px]'}`}
                  >
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Preview
                        capturePreview={capturePreview}
                        adjustments={filter.adjustments}
                        filter={filter._id}
                        ratio={1}
                        cameraRatio={1}
                        previewQuality={{
                          maxWidth: 360,
                          maxHeight: 360,
                          quality: 0.8
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <button
          onClick={slideNext}
          className="rounded-full bg-carnabyTan py-6 pr-5 pl-7 mx-8 hover:opacity-90 transition-opacity"
          aria-label="Next filter"
        >
          <FaPlay className="text-juicyMango w-8 h-8" />
        </button>
      </div>
      <div className="absolute bottom-[100px] right-[105px] w-[380px]">
        <div className="flex items-center flex-col gap-4 justify-center">
          {remainingTime !== '--:--' && (
            <span className="text-white text-2xl">{remainingTime}</span>
          )}
          <button
            onClick={continueToPhotoSession}
            className="w-full font-body-samu rounded-lg bg-carnabyTan text-white px-20 tracking-widest text-5xl py-8 transition-transform transform shadow-xl hover:brightness-110 active:brightness-110"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  )
}

export default FilterPage
